import { wait } from '../ui/lib/wait';

type RetryOptions = {
  /** Determine if an error is cause for a "fail" or a "retry" */
  canRetryOnError: (error: unknown) => boolean;
  /** Execution of retries will be delayed by the value of this function's result (default=100ms) */
  delay: (remainingRetries: number) => number;
  /** Callback on completion with number of attempts made */
  onComplete: (retries: number) => void;
  /** Callback on retry call with the current attempt number */
  onRetry: (retries: number) => void;
  /** Number of times to retry (default=3) */
  retries: number;
};

type RetryExecutor<T = unknown> = {
  (): Promise<{ status: 'success'; result: T } | { status: 'retry' | 'fail' }>;
};

/**
 * Retry a promise/executor until it succeeds, fails, or we cannot retry anymore.
 *
 * The executor should either:
 *
 * - Return `{ status: 'success', result: 'my successful result' }`
 * - Return `{ status: 'retry' }` when the result isn't satisfactory and we want
 *   to run the executor another time.
 * - Return `{ status: 'fail' } to stop execution because of an unacceptable value
 * - Throw an error
 *
 * Errors from the executor can either be treated as a failure (default) or you can pass
 * an option to inspect errors that should be treated as retry-able:
 *
 * ```typescript
 * retry(async () => { throw new Error('test') }, {
 *   // errors with the message 'test' will be retried, other errors will still fail
 *   canRetryOnError: (error) => error instanceof Error && error.message === 'test'
 * })
 * ```
 */
export async function retry<T = unknown>(
  executor: RetryExecutor<T>,
  options: Partial<RetryOptions> = {},
): Promise<T> {
  return await new RetryExecution(executor, options).retry();
}

class RetryExecution<T = unknown> {
  attempts = 0;
  executor: RetryExecutor<T>;
  retries = 3;
  delay = (_attempt: number) => 100;
  options: Partial<RetryOptions> = {};

  constructor(executor: RetryExecutor<T>, options: Partial<RetryOptions>) {
    this.executor = executor;
    if (options.retries) {
      this.retries = options.retries;
    }
    if (options.delay) {
      this.delay = options.delay;
    }

    this.options = options;
  }

  async retry(): Promise<T> {
    if (this.attempts >= this.retries) {
      this.notifyComplete();
      return Promise.reject(new ExceededRetryAttemptsError());
    }

    try {
      // execute!
      const executorResult = await this.executor();
      if (executorResult.status === 'retry') {
        throw new RetryError();
      }
      if (executorResult.status === 'fail') {
        throw new FailRetryError('Executor returned fail status');
      }
      if (executorResult.status === 'success') {
        this.notifyComplete();
        return executorResult.result;
      }
      throw new Error(`Invalid status returned by executor: ${executorResult.status}`);
    } catch (error) {
      // executor return { status: fail } so we don't need to continue
      if (error instanceof FailRetryError) {
        this.notifyComplete();
        return Promise.reject(error);
      }

      // check if we still having remaining retries available...
      if (this.attempts < this.retries) {
        // RetryError is used to automatically trigger a retry
        // from the executor returning { status: retry }
        let canRetry = error instanceof RetryError;

        if (!canRetry) {
          // see if this particular error is retry-able
          canRetry = this.canRetryOnError(error);
          if (!canRetry) {
            this.notifyComplete();
            // we don't like this error, so just reject
            return Promise.reject(error);
          }
        }

        // finally, we can try again...
        if (canRetry) {
          this.attempts += 1;
          this.notifyRetry();
          await wait(this.delay(this.attempts));
          return this.retry();
        }
      }

      this.notifyComplete();
      return Promise.reject(new ExceededRetryAttemptsError());
    }
  }

  protected canRetryOnError(error: unknown) {
    if (this.options.canRetryOnError) {
      return this.options.canRetryOnError(error);
    }
    return false;
  }

  protected notifyComplete() {
    this.options.onComplete?.(this.attempts);
  }

  protected notifyRetry() {
    this.options.onRetry?.(this.attempts);
  }
}

class RetryError extends Error {}

export class FailRetryError extends Error {}

export class ExceededRetryAttemptsError extends Error {
  name = 'ExceededRetryAttemptsError';
}
